import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ActionPopoverButton, {
  ActionPopoverElement,
} from '../../components/ActionPopoverButton';
import Gravatar from '../../user/components/Gravatar';
import { formatTime } from '../../utils/date';
import {
  OpenClass,
  OpenClassSession,
  OpenClassStatus,
} from '../types/OpenClass';
import { sessionSpansDays } from '../utils/openClass';
import { SessionOutcome } from '../utils/sessionOutcome';
import DetailField from './DetailField';
import OpenClassStatusBadge from './OpenClassStatusBadge';

type OpenClassSessionDetailsCardProps = {
  actions: ActionPopoverElement[];
  course: OpenClass['course'];
  outcome?: SessionOutcome;
  session: OpenClassSession;
  showActions: boolean;
  onShowAttendees: () => void;
};

const OpenClassSessionDetailsCard: React.FC<
  OpenClassSessionDetailsCardProps
> = ({ actions, course, outcome, session, showActions, onShowAttendees }) => {
  const { t } = useTranslation();
  const startsAt = dayjs(session.startAt);
  const endsAt = dayjs(session.endAt);
  const endsOnAnotherDay = sessionSpansDays(session);

  return (
    <div
      className={
        session.status === OpenClassStatus.Canceled ? 'opacity-60' : ''
      }>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-courseInstanceIncomplete px-4 py-1 font-medium text-white">
            {dayjs(session.startAt).format('DD MMMM YYYY')}
          </span>
          <span className="font-semibold">
            {outcome?.registered ?? 0} / {session.capacity}
          </span>
          {!!outcome?.notPresent && (
            <span className="text-blueGrayDark">
              (
              {t('views.OpenClasses.notPresentCount', {
                count: outcome.notPresent,
              })}
              )
            </span>
          )}
        </div>
        {showActions && (
          <ActionPopoverButton
            absolutePlacement={false}
            actionPopoverElements={actions}
            placement="bottomRight"
          />
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <OpenClassStatusBadge endAt={session.endAt} status={session.status} />
        <span>{course.name}</span>
      </div>

      <div className="grid grid-cols-1 gap-4 border-0 border-t border-solid border-gray-200 pt-4 md:grid-cols-3">
        <div className="space-y-3">
          <DetailField
            variant="heading"
            label={t('views.OpenClasses.visitingAddress')}>
            {session.location || '–'}
          </DetailField>
          {session.comment && (
            <DetailField variant="heading" label={t('common.note')}>
              {session.comment}
            </DetailField>
          )}
        </div>
        <DetailField variant="heading" label={t('views.OpenClasses.time')}>
          <div>{startsAt.format('dddd, DD MMMM YYYY')}</div>
          <div>
            {formatTime(session.startAt)} – {formatTime(session.endAt)}
            {endsOnAnotherDay && ` (${endsAt.format('dddd, DD MMMM YYYY')})`}
          </div>
        </DetailField>
        <DetailField variant="heading" label={t('common.instructors')}>
          {session.instructors.length === 0 && <div>–</div>}
          {session.instructors.map((instructor) => (
            <div
              key={instructor.id ?? instructor.email}
              className="mb-3 flex items-start gap-2">
              <Gravatar
                email={instructor.email}
                internal={instructor.internal}
                isInstructor
                size="small"
              />
              <div>
                <div>{instructor.name}</div>
                <div className="text-xs text-blueGrayDark">
                  {instructor.email}
                </div>
                {instructor.phoneNumber && (
                  <div className="text-xs text-blueGrayDark">
                    {instructor.phoneNumber}
                  </div>
                )}
              </div>
            </div>
          ))}
        </DetailField>
      </div>

      <div className="mt-4 flex items-start justify-between gap-3 border-0 border-t border-solid border-gray-200 pt-4">
        <div>
          <div className="mb-2 font-semibold">
            {t('views.OpenClasses.attendees')}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <TeamOutlined className="text-safeLifeMedium" />
              {t('views.OpenClasses.attendedCount', {
                attended: outcome?.present ?? 0,
                registered: outcome?.registered ?? 0,
              })}
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-safeLifeSuccess" />
              {t('views.OpenClasses.passedCount', {
                count: outcome?.passed ?? 0,
              })}
              {!!outcome?.passed && (
                <Tooltip
                  title={t('views.OpenClasses.certificatesEmailedCount')}>
                  <span className="flex items-center gap-1 text-blueGrayDark">
                    ({outcome.certificatesSent} <MailOutlined />)
                  </span>
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-2">
              <CloseCircleOutlined className="text-warningRed" />
              {t('views.OpenClasses.failedCount', {
                count: outcome?.failed ?? 0,
              })}
            </div>
          </div>
        </div>
        <Button onClick={onShowAttendees}>
          {t('views.OpenClasses.attendeesOn', {
            date: dayjs(session.startAt).format('D MMMM'),
          })}
        </Button>
      </div>
    </div>
  );
};

export default OpenClassSessionDetailsCard;
